<template>
  <div class="country-dropdown-root">
    <div class="p-float-label">
      <CustomDropdown
          :id="inputId"
          :disabled="disabled"
          :loading="countriesDataIsLoading"
          :class="[
            { 'p-invalid': invalid || (submitted && countryIsInvalid) },
            { 'p-inputtext-lg': isLarge }
          ]"
          v-model="localCountry"
          :clearSearchData="!visible"
          :options="countries"
          :filter="true"
          :filterFields="filterFields"
          :showClear="showClear"
          :optionLabel="optionLabel"
          @change="changeCountry">
<!--        <template #value="slotProps">-->
<!--          <div v-if="slotProps.value">-->
<!--            <span>{{ slotProps.value[optionLabel] }}<span v-if="slotProps.value.name_native"> ({{slotProps.value.name_native}})</span></span>-->
<!--          </div>-->
<!--          <span v-else style="visibility: hidden">.</span>-->
<!--        </template>-->

<!--        <template #option="slotProps">-->
<!--          <div>-->
<!--            <div>{{ slotProps.option[optionLabel] }}<span v-if="slotProps.option.name_native"> ({{slotProps.option.name_native}})</span></div>-->
<!--          </div>-->
<!--        </template>-->
        <template #value="slotProps">
          <div v-if="slotProps.value">
            {{ countryLabel(slotProps.value) }}
          </div>
          <span v-else style="visibility: hidden">.</span>
        </template>

        <template #option="slotProps">
          <div v-if="slotProps.option">
            {{ countryLabel(slotProps.option) }}
          </div>
        </template>
      </CustomDropdown>

      <label :for="inputId">
        {{ $t(label) }}<span v-show="required" class="warning-color">*</span>
      </label>
    </div>

    <small class="p-invalid" v-if="submitted && required && countryIsInvalid">
      {{ $t('Required field') }}
    </small>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import showErrorsMixins from "@/mixins/showErrorsMixins";
import settings from '@/settings';

export default {
  mixins: [showErrorsMixins],
  name: 'CountryDropdown',
  emits: [
    'update:modelValue',
    'update:isInvalid',
    'loaded',
    'change',
  ],
  props: {
    modelValue: { type: Object, default: null },

    countryId: { type: [Number, String], default: null },

    countryCode: { type: String, default: null },

    invalid: { type: Boolean, default: false },

    submitted: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    isLarge: { type: Boolean, default: false },
    visible: { type: Boolean, default: true }, // for clearSearchData

    inputId: { type: String, default: 'country-dropdown' },
    label: { type: String, default: 'Country' },

    showClear: { type: Boolean, default: false },
    optionLabel: { type: String, default: 'name_en' },
    filterFields: {
      type: Array,
      default: () => [
        'name_en',
        'name_native',
        'code',
      ],
    },

    autoLoad: { type: Boolean, default: true },
    autoSelectDefault: { type: Boolean, default: true }, // only when no countryId and no modelValue
    resetOnClose: { type: Boolean, default: false },
  },

  data() {
    return {
      countries: [],
      countriesDataIsLoading: false,
    }
  },

  watch: {
    countryIsInvalid: {
      immediate: true,
      handler(v) {
        this.$emit('update:isInvalid', v)
      },
    },

    countryId: {
      handler() {
        this.applyInitialCountry()
      },
    },

    countryCode: {
      handler() {
        this.applyInitialCountry()
      },
    },

    visible: {
      async handler(v) {
        if (!this.autoLoad) return

        if (!v) {
          if (this.resetOnClose) this.localCountry = null
          return
        }

        if (!this.countries.length) {
          await this.getCountries()
          return
        }

        this.applyInitialCountry()
        this.applyDefaultIfNeeded()
      },
    },
  },

  mounted() {
    if (this.autoLoad && this.visible) {
      this.getCountries()
    }
  },

  methods: {
    countryLabel(country) {
      const code = String(country?.code || '').toUpperCase()
      const englishName = country?.name_en || code
      const nativeName = country?.name_native || null

      const namesAreDifferent =
          nativeName
          && nativeName.trim().toLocaleLowerCase()
          !== englishName.trim().toLocaleLowerCase()

      const name = namesAreDifferent ? `${englishName} — ${nativeName}` : englishName

      return code ? `${name} (${code})` : name
    },

    sortCountries(countries) {
      const priorityCodes =
          Array.isArray(settings.priorityCountryCodes)
              ? settings.priorityCountryCodes.map(
                  code => String(code || '').toUpperCase()
              )
              : []

      return [...countries].sort((a, b) => {
        const aCode = String(a.code || '').toUpperCase()
        const bCode = String(b.code || '').toUpperCase()

        const aIndex = priorityCodes.indexOf(aCode)
        const bIndex = priorityCodes.indexOf(bCode)

        const aPriority =
            aIndex === -1 ? priorityCodes.length : aIndex

        const bPriority =
            bIndex === -1 ? priorityCodes.length : bIndex

        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }

        return this.countryLabel(a).localeCompare(
            this.countryLabel(b),
            'en'
        )
      })
    },

    changeCountry(event) {
      this.$emit('change', event?.value ?? null)
    },
    async getCountries() {
      if (this.countriesDataIsLoading || !this.$store.getters.userCanGetData) return
      this.countriesDataIsLoading = true

      try {
        const { data, status } = await httpClient('country/modal-countries')
        if (status === 200 && Array.isArray(data)) {
          this.countries = this.sortCountries(data)
          this.$emit('loaded', this.countries)

          await this.$nextTick()

          this.applyInitialCountry()
          this.applyDefaultIfNeeded()
        } else {
          this.countries = []
          this.showUnknownError()
        }
      } catch(err) {
        this.countries = []
        this.showError(err)
      } finally {
        this.countriesDataIsLoading = false
      }
    },

    applyInitialCountry() {
      if (!this.countries.length) {
        return
      }

      let country = null

      if (this.countryId !== null && this.countryId !== undefined && this.countryId !== '') {
        country = this.countries.find(item => Number(item.id) === Number(this.countryId)) ?? null
      } else if (this.countryCode) {
        const countryCode = String(this.countryCode).toUpperCase()
        country = this.countries.find(item => String(item.code || '').toUpperCase() === countryCode) ?? null
      }

      if (country && Number(this.localCountry?.id) !== Number(country.id)) {
        this.localCountry = country
      }
    },

    applyDefaultIfNeeded() {
      const hasCountryId =
          this.countryId !== null
          && this.countryId !== undefined
          && this.countryId !== ''

      const hasCountryCode = Boolean(this.countryCode)

      if (hasCountryId || hasCountryCode) {
        return
      }

      if (!this.autoSelectDefault) {
        return
      }

      if (this.localCountry || !this.countries.length) {
        return
      }

      const defaultCountry = this.countries.find(country => Number(country.is_default)) ?? null

      if (defaultCountry) {
        this.localCountry = defaultCountry
      }
    },
  },
  computed: {
    localCountry: {
      get() {
        return this.modelValue
      },
      set(v) {
        this.$emit('update:modelValue', v)
      },
    },

    countryIsInvalid() {
      if (!this.required) return false
      return !this.localCountry
    },
  },
}
</script>